import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_RECORDING_BYTES,
  MAX_RECORDING_SECONDS,
  isVoiceRecordingSupported,
  pickSupportedAudioMimeType,
  transcribeRecordedAudio,
} from '@/lib/assistant-voice';

export type VoiceRecorderStatus = 'idle' | 'recording' | 'transcribing';

export interface UseVoiceRecorderResult {
  status: VoiceRecorderStatus;
  /** Segundos decorridos da gravacao atual (0 fora de 'recording'). */
  elapsedSeconds: number;
  /** Mensagem de erro amigavel (permissao negada, sem suporte, falha na transcricao). */
  error: string | null;
  /** Inicia a gravacao (pede permissao de microfone). */
  start: () => void;
  /** Para a gravacao e envia para transcricao. */
  stop: () => void;
  /** Para a gravacao e DESCARTA o audio (nao transcreve). */
  cancel: () => void;
  clearError: () => void;
}

/** Traduz o erro de getUserMedia numa mensagem util ao usuario. */
function describeGetUserMediaError(err: unknown): string {
  const name = err instanceof DOMException ? err.name : '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return 'Permissão de microfone negada. Habilite o acesso ao microfone nas configurações do navegador para gravar áudio.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'Nenhum microfone encontrado neste dispositivo.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'Não foi possível acessar o microfone (ele pode estar em uso por outro aplicativo).';
  }
  return 'Não foi possível acessar o microfone. Tente novamente.';
}

/**
 * Orquestra a gravacao de audio no chat do assistente (rodada 4 "anexos e
 * voz"): pede o microfone, grava com MediaRecorder, e ao parar envia para
 * transcricao — devolvendo o texto via `onTranscribed` para o widget colocar
 * no input (NUNCA envia a mensagem sozinho).
 *
 * Estados: 'idle' -> 'recording' (apos permissao concedida) -> 'transcribing'
 * (apos parar, aguardando o backend) -> 'idle'. `cancel()` sai de 'recording'
 * direto para 'idle', descartando o audio sem chamar a API.
 *
 * Libera o microfone (stream.getTracks().stop()) em TODO caminho de saida de
 * 'recording' — parar, cancelar, erro, ou desmontagem do componente — para
 * nunca deixar o indicador de gravacao do browser ligado escondido.
 */
export function useVoiceRecorder(onTranscribed: (text: string) => void): UseVoiceRecorderResult {
  const [status, setStatus] = useState<VoiceRecorderStatus>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discardRef = useRef(false);
  const onTranscribedRef = useRef(onTranscribed);
  onTranscribedRef.current = onTranscribed;

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  // Libera o microfone se o componente desmontar (ou o widget fechar, se o
  // caller desmontar o hook) no meio de uma gravacao.
  useEffect(
    () => () => {
      stopTimer();
      releaseStream();
    },
    [releaseStream, stopTimer],
  );

  const finishRecording = useCallback(
    async (discard: boolean) => {
      stopTimer();
      const elapsed = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
      releaseStream();

      const chunks = chunksRef.current;
      chunksRef.current = [];

      if (discard || chunks.length === 0) {
        setStatus('idle');
        setElapsedSeconds(0);
        return;
      }

      const mimeType = recorderRef.current?.mimeType || 'audio/webm';
      const blob = new Blob(chunks, { type: mimeType });

      if (blob.size === 0) {
        setStatus('idle');
        setElapsedSeconds(0);
        return;
      }

      if (blob.size > MAX_RECORDING_BYTES) {
        setError('Áudio muito grande. Grave uma mensagem mais curta.');
        setStatus('idle');
        setElapsedSeconds(0);
        return;
      }

      setStatus('transcribing');
      try {
        const { text } = await transcribeRecordedAudio(blob, elapsed);
        if (text.trim()) onTranscribedRef.current(text.trim());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Não foi possível transcrever o áudio.');
      } finally {
        setStatus('idle');
        setElapsedSeconds(0);
      }
    },
    [releaseStream, stopTimer],
  );

  const start = useCallback(() => {
    setError(null);
    if (!isVoiceRecordingSupported()) {
      setError('Gravação de áudio não é suportada neste navegador.');
      return;
    }

    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const mimeType = pickSupportedAudioMimeType();
        const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        recorderRef.current = recorder;
        chunksRef.current = [];
        discardRef.current = false;

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunksRef.current.push(event.data);
        };
        recorder.onstop = () => {
          void finishRecording(discardRef.current);
        };

        recorder.start();
        startedAtRef.current = Date.now();
        setStatus('recording');
        setElapsedSeconds(0);

        timerRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
          setElapsedSeconds(elapsed);
          // Auto-stop ao atingir a duracao maxima — evita gravar alem do que
          // o servidor aceitaria e transcrever o que ja foi dito ate ali.
          if (elapsed >= MAX_RECORDING_SECONDS) {
            stopTimer();
            recorderRef.current?.stop();
          }
        }, 250);
      } catch (err) {
        releaseStream();
        setStatus('idle');
        setError(describeGetUserMediaError(err));
      }
    })();
  }, [finishRecording, releaseStream, stopTimer]);

  const stop = useCallback(() => {
    if (status !== 'recording') return;
    discardRef.current = false;
    recorderRef.current?.stop();
  }, [status]);

  const cancel = useCallback(() => {
    if (status !== 'recording') return;
    discardRef.current = true;
    recorderRef.current?.stop();
  }, [status]);

  const clearError = useCallback(() => setError(null), []);

  return { status, elapsedSeconds, error, start, stop, cancel, clearError };
}
